/**
 * RegolamentoEditorPage — editor completo del regolamento fantapunti.
 *
 * Sezioni:
 *   - Master flags (modificatore difesa, fairplay, voto rinvio, max sost., panchinari)
 *   - Formazione & sostituzioni (sv_comportamento, panchina_struttura, formazione_non_schierata)
 *   - Spareggi KO (tipo, supplementari modalita + n_panchinari)
 *   - Bonus / Malus eventi (lista add/remove)
 *   - Soglie gol da fantapunti (lista add/remove)
 *   - Bonus capitano (lista add/remove)
 *   - Modificatore difesa (min difensori, ruoli mantra, soglie media voto)
 *   - Fairplay (lista regole con bonus)
 *
 * Funzionalita extra:
 *   - "Carica preset FantaSquad standard" — popola il draft con i valori canonici.
 *   - "Modifica forzata (admin)" — se il regolamento e' congelato, l'admin puo'
 *     comunque salvare passando ?force=true al backend.
 *   - 404 → mostra valori default per creare al primo salvataggio.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { etichettaFascia } from "../common/fasce";
import { etichetta } from "../common/etichette";
import { StagioneSelector } from "../common/StagioneSelector";
import {
  NOME_BONUS_DIFENSIVO,
  NOME_MODALITA_PRO,
  siglaRuolo,
  sigleRuoli,
} from "../common/nomenclatura";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const EVENTI = [
  "gol",
  "gol_portiere",
  "assist",
  "ammonizione",
  "espulsione",
  "autogoal",
  "rigore_segnato",
  "rigore_sbagliato",
  "rigore_parato",
  "portiere_imbattuto",
  "gol_vittoria",
  "gol_pareggio",
  "gol_subito_portiere",
] as const;

const EVENTO_LABEL: Record<string, string> = {
  gol: tr("Gol"),
  gol_portiere: tr("Gol portiere"),
  assist: tr("Assist"),
  ammonizione: tr("Ammonizione"),
  espulsione: tr("Espulsione"),
  autogoal: tr("Autogol"),
  rigore_segnato: tr("Rigore segnato"),
  rigore_sbagliato: tr("Rigore sbagliato"),
  rigore_parato: tr("Rigore parato"),
  portiere_imbattuto: tr("Portiere imbattuto"),
  gol_vittoria: tr("Gol vittoria"),
  gol_pareggio: tr("Gol pareggio"),
  gol_subito_portiere: tr("Gol subito (portiere)"),
};

const SV_COMPORTAMENTO_OPTIONS = [
  { v: "sostituzione_ruolo_fedele", l: tr("Sostituzione ruolo fedele") },
  { v: "sostituzione_ruolo_libero", l: tr("Sostituzione ruolo libero") },
  { v: "voto_base_6", l: tr("Voto base 6") },
  { v: "nullo", l: tr("Nullo (ignora)") },
];

const PANCHINA_STRUTTURA_OPTIONS = [
  { v: "lista_ordinata", l: tr("Lista ordinata") },
  { v: "per_ruolo", l: tr("Per ruolo") },
];

const FORMAZIONE_NON_SCHIERATA_OPTIONS = [
  { v: "punteggio_fisso", l: tr("Punteggio fisso") },
  { v: "zero", l: tr("Zero") },
  { v: "sconfitta_automatica", l: tr("Sconfitta automatica") },
];

const SPAREGGIO_KO_OPTIONS = [
  { v: "diff_reti_torneo", l: tr("Differenza reti torneo") },
  { v: "piazzamento_stagione", l: tr("Piazzamento stagione") },
  { v: "supplementari", l: tr("Supplementari") },
];

const SPAREGGIO_SUPP_MODALITA_OPTIONS = [
  { v: "somma_fantapunti", l: tr("Somma fantapunti") },
  { v: "media_voto_base", l: tr("Media voto base") },
];

const MANTRA_RUOLI = ["Por", "Dc", "Dd", "Ds", "B", "E", "M", "C", "W", "T", "A", "Pc"] as const;

interface BonusMalusItem {
  evento: (typeof EVENTI)[number];
  punteggio: string;
}
interface SogliaGolItem {
  punteggio_minimo: string;
  gol_assegnati: number;
}
interface BonusCapitanoItem {
  voto_base_minimo: string;
  bonus_capitano: string;
}
interface ModificatoreDifesaItem {
  media_voto_minimo: string;
  bonus: string;
}
interface FairplayItem {
  descrizione: string;
  zero_ammonizioni: boolean;
  zero_espulsioni: boolean;
  bonus: string;
}

interface RegolamentoFull {
  regolamento_id: string | null;
  stagione_id: string;
  congelato_at: string | null;
  modificatore_difesa_attivo: boolean;
  modificatore_difesa_min_difensori: number;
  modificatore_difesa_includi_portiere: boolean;
  mantra_ruoli_difesa: string[];
  sv_comportamento: string;
  panchina_struttura: string;
  numero_panchinari: number;
  max_sostituzioni: number | null;
  formazione_non_schierata: string;
  formazione_non_schierata_punteggio_fisso: string | null;
  fairplay_attivo: boolean;
  voto_giocatori_rinvio_sa: string;
  spareggio_ko: string;
  spareggio_supplementari_attivo: boolean;
  spareggio_supplementari_n_panchinari: number;
  spareggio_supplementari_modalita: string;
  bonus_malus: BonusMalusItem[];
  soglie_gol: SogliaGolItem[];
  bonus_capitano: BonusCapitanoItem[];
  modificatore_difesa: ModificatoreDifesaItem[];
  fairplay: FairplayItem[];
}

// =====================================================================
// Preset standard FantaSquad — copiabile con un click
// =====================================================================
function presetStandardV1(stagioneId: string): RegolamentoFull {
  // Andrea 2026-09-11: e' la fotografia del regolamento della stagione
  // 2026-27 cosi' com'era in produzione, da ricaricare
  // tale e quale nelle stagioni a venire. Se cambia una regola in
  // stagione, va cambiata anche qui.
  return {
    regolamento_id: null,
    stagione_id: stagioneId,
    congelato_at: null,
    modificatore_difesa_attivo: true,
    modificatore_difesa_min_difensori: 5,
    modificatore_difesa_includi_portiere: true,
    mantra_ruoli_difesa: ["Dc", "Dd", "Ds", "B", "M", "E"],
    sv_comportamento: "sostituzione_ruolo_fedele",
    panchina_struttura: "lista_ordinata",
    numero_panchinari: 8,
    max_sostituzioni: 5,
    formazione_non_schierata: "punteggio_fisso",
    formazione_non_schierata_punteggio_fisso: "60.0",
    fairplay_attivo: false,
    voto_giocatori_rinvio_sa: "6.0",
    spareggio_ko: "diff_reti_torneo",
    spareggio_supplementari_attivo: true,
    spareggio_supplementari_n_panchinari: 3,
    spareggio_supplementari_modalita: "somma_fantapunti",
    bonus_malus: [
      { evento: "ammonizione", punteggio: "-0.5" },
      { evento: "assist", punteggio: "1.0" },
      { evento: "autogoal", punteggio: "-2.0" },
      { evento: "espulsione", punteggio: "-1.0" },
      { evento: "gol", punteggio: "3.0" },
      { evento: "gol_portiere", punteggio: "3.0" },
      { evento: "gol_subito_portiere", punteggio: "-1.0" },
      { evento: "portiere_imbattuto", punteggio: "1.0" },
      { evento: "rigore_parato", punteggio: "3.0" },
      { evento: "rigore_sbagliato", punteggio: "-3.0" },
      { evento: "rigore_segnato", punteggio: "3.0" },
    ],
    // Da 65 a 135 punti, un gol ogni 5.
    soglie_gol: [
      { punteggio_minimo: "65.0", gol_assegnati: 1 },
      { punteggio_minimo: "70.0", gol_assegnati: 2 },
      { punteggio_minimo: "75.0", gol_assegnati: 3 },
      { punteggio_minimo: "80.0", gol_assegnati: 4 },
      { punteggio_minimo: "85.0", gol_assegnati: 5 },
      { punteggio_minimo: "90.0", gol_assegnati: 6 },
      { punteggio_minimo: "95.0", gol_assegnati: 7 },
      { punteggio_minimo: "100.0", gol_assegnati: 8 },
      { punteggio_minimo: "105.0", gol_assegnati: 9 },
      { punteggio_minimo: "110.0", gol_assegnati: 10 },
      { punteggio_minimo: "115.0", gol_assegnati: 11 },
      { punteggio_minimo: "120.0", gol_assegnati: 12 },
      { punteggio_minimo: "125.0", gol_assegnati: 13 },
      { punteggio_minimo: "130.0", gol_assegnati: 14 },
      { punteggio_minimo: "135.0", gol_assegnati: 15 },
    ],
    // Fattore capitano stile Fantagazzetta: simmetrico sul voto del
    // capitano, mezzo punto per gradino, da -2 (voto <= 4) a +2 (>= 8).
    // La riga con minimo 0 e' il fondo scala.
    bonus_capitano: [
      { voto_base_minimo: "0.0", bonus_capitano: "-2.0" },
      { voto_base_minimo: "4.5", bonus_capitano: "-1.5" },
      { voto_base_minimo: "5.0", bonus_capitano: "-1.0" },
      { voto_base_minimo: "5.5", bonus_capitano: "-0.5" },
      { voto_base_minimo: "6.0", bonus_capitano: "0.0" },
      { voto_base_minimo: "6.5", bonus_capitano: "0.5" },
      { voto_base_minimo: "7.0", bonus_capitano: "1.0" },
      { voto_base_minimo: "7.5", bonus_capitano: "1.5" },
      { voto_base_minimo: "8.0", bonus_capitano: "2.0" },
    ],
    // D Factor: fasce da 0.25 di media difesa, da 0 (< 6) a +3 (>= 7).
    modificatore_difesa: [
      { media_voto_minimo: "0.00", bonus: "0.0" },
      { media_voto_minimo: "6.00", bonus: "0.5" },
      { media_voto_minimo: "6.25", bonus: "1.0" },
      { media_voto_minimo: "6.50", bonus: "1.5" },
      { media_voto_minimo: "6.75", bonus: "2.0" },
      { media_voto_minimo: "7.00", bonus: "3.0" },
    ],
    fairplay: [
      {
        descrizione: tr("Tutta la squadra senza ammonizioni"),
        zero_ammonizioni: true,
        zero_espulsioni: false,
        bonus: "1.0",
      },
      {
        descrizione: tr("Tutta la squadra senza espulsioni"),
        zero_ammonizioni: false,
        zero_espulsioni: true,
        bonus: "1.0",
      },
    ],
  };
}

function defaultRegolamento(stagioneId: string): RegolamentoFull {
  // Default minimale: stessi valori del preset ma senza preset di esempio
  // (bonus_malus base, niente soglie/capitano/fairplay).
  return {
    ...presetStandardV1(stagioneId),
    soglie_gol: [],
    bonus_capitano: [],
    modificatore_difesa: [],
    fairplay: [],
  };
}

// =====================================================================
// Componente
// =====================================================================
export function RegolamentoEditorPage() {
  useAuth(); // session check
  const qc = useQueryClient();
  const [stagioneId, setStagioneId] = useState("");
  const [draft, setDraft] = useState<RegolamentoFull | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [forceMode, setForceMode] = useState(false);
  // Andrea 2026-09-09: soglie gol in automatico — si dice da quanti
  // fantapunti arriva il primo gol e ogni quanti ne arriva un altro.
  const [sogliaPrimoGol, setSogliaPrimoGol] = useState("66");
  const [passoGol, setPassoGol] = useState("6");
  const [quantiGol, setQuantiGol] = useState("8");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "regolamento", stagioneId],
    queryFn: async () => {
      const res = await api.get<RegolamentoFull>(`/admin/regolamento/${stagioneId}`);
      return res.data;
    },
    enabled: !!stagioneId,
    retry: false,
  });

  useEffect(() => {
    if (data) {
      setDraft(data);
      setIsNew(false);
    } else if (
      stagioneId &&
      isError &&
      (error as { response?: { status?: number } })?.response?.status === 404
    ) {
      setDraft(defaultRegolamento(stagioneId));
      setIsNew(true);
    }
  }, [data, stagioneId, isError, error]);

  useEffect(() => {
    setDraft(null);
    setIsNew(false);
    setForceMode(false);
  }, [stagioneId]);

  const saveMut = useMutation({
    mutationFn: async (payload: RegolamentoFull) => {
      const url = forceMode
        ? `/admin/regolamento/${stagioneId}?force=true`
        : `/admin/regolamento/${stagioneId}`;
      const res = await api.put<RegolamentoFull>(url, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "regolamento", stagioneId] });
      qc.invalidateQueries({ queryKey: ["regolamento", stagioneId] });
      alert(tr("✓ Regolamento salvato"));
      refetch();
    },
    onError: (e: { response?: { data?: { detail?: string } }; message?: string }) => {
      alert(
        tr("Errore: {{v1}}", {
          v1: e.response?.data?.detail ?? e.message ?? "salvataggio fallito",
        }),
      );
    },
  });

  const isCongelato = draft?.congelato_at !== null && draft?.congelato_at !== undefined;
  const editLocked = isCongelato && !forceMode;

  function update<K extends keyof RegolamentoFull>(key: K, value: RegolamentoFull[K]): void {
    if (!draft) return;
    setDraft({ ...draft, [key]: value });
  }

  /** Rigenera la scaletta delle soglie gol: primo gol da X, poi uno ogni
   * Y fantapunti, per Z gol. Sostituisce la lista attuale (Andrea
   * 2026-09-09: scriverle a mano una per una era una perdita di tempo). */
  function generaSoglieGol(): void {
    if (!draft) return;
    const partenza = parseFloat(sogliaPrimoGol);
    const passo = parseFloat(passoGol);
    const quanti = parseInt(quantiGol, 10);
    if (!isFinite(partenza) || !isFinite(passo) || passo <= 0) return;
    if (!Number.isFinite(quanti) || quanti < 1 || quanti > 30) return;
    if (
      draft.soglie_gol.length > 0 &&
      !confirm(
        tr("Le {{v1}} soglie attuali verranno sostituite da {{v2}} soglie generate. Procedere?", {
          v1: draft.soglie_gol.length,
          v2: quanti,
        }),
      )
    )
      return;
    update(
      "soglie_gol",
      Array.from({ length: quanti }, (_, i) => ({
        punteggio_minimo: (partenza + passo * i).toFixed(1),
        gol_assegnati: i + 1,
      })),
    );
  }

  function caricaPreset(): void {
    if (!stagioneId) return;
    if (
      draft &&
      !isNew &&
      !confirm(
        tr(
          "Caricare il preset standard sovrascriverà i valori attuali (non ancora salvati). Procedere?",
        ),
      )
    )
      return;
    const preset = presetStandardV1(stagioneId);
    // Preserva congelato_at + regolamento_id se in editing
    setDraft({
      ...preset,
      regolamento_id: draft?.regolamento_id ?? null,
      congelato_at: draft?.congelato_at ?? null,
    });
  }

  return (
    <MobileShell logoSuffix="REGOLAMENTO">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Editor regolamento fantapunti")}
      </h2>

      <div className="card">
        <StagioneSelector value={stagioneId} onChange={setStagioneId} />
      </div>

      {isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {draft && (
        <>
          {isNew && (
            <div className="card bg-info/10 border border-info/40 text-info text-sm">
              ℹ Nessun regolamento ancora salvato per questa stagione. Stai vedendo i valori di
              default — modifica e clicca "Salva regolamento" per crearlo.
            </div>
          )}

          {isCongelato && (
            <div className="card bg-danger/10 border border-danger/40 space-y-2">
              <div className="text-danger font-bold">
                🔒 Regolamento CONGELATO dal{" "}
                {new Date(draft.congelato_at!).toLocaleString(localeCorrente())}
              </div>
              <div className="text-xs text-fg">
                {tr(
                  "Sono già stati calcolati voti per la stagione. Modificare può alterare retroattivamente le classifiche delle giornate già emesse — usa con cautela.",
                )}
              </div>
              <label className="flex items-center gap-2 text-sm pt-2 border-t border-danger/20">
                <input
                  type="checkbox"
                  checked={forceMode}
                  onChange={(e) => setForceMode(e.target.checked)}
                />
                <span>
                  <strong>{tr("Modifica forzata (admin)")}</strong>{" "}
                  {tr("— sblocca il salvataggio ignorando il blocco congelamento.")}
                </span>
              </label>
            </div>
          )}

          {/* Preset loader */}
          <div className="card flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <strong className="text-accent">{tr("📋 Preset FantaSquad standard v1")}</strong>
              <div className="text-xs text-fg-muted mt-1">
                {tr(
                  "Carica tutti i valori canonici: bonus/malus base, soglie gol 66/72/78/84/90, bonus capitano ≥8.0/9.0, modificatore difesa 6.0/6.5/7.0, fairplay no-ammoniz./no-espuls.",
                )}
              </div>
            </div>
            <button
              type="button"
              disabled={editLocked}
              onClick={caricaPreset}
              className="btn-ghost text-sm border border-accent/40 text-accent hover:bg-accent/10"
            >
              {tr("Carica preset")}
            </button>
          </div>

          {/* ============================================================ */}
          {/* MASTER FLAGS */}
          {/* ============================================================ */}
          <div className="card grid grid-cols-1 md:grid-cols-2 gap-3">
            <h3 className="font-display text-lg text-accent col-span-full">{tr("Generale")}</h3>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.modificatore_difesa_attivo}
                disabled={editLocked}
                onChange={(e) => update("modificatore_difesa_attivo", e.target.checked)}
              />
              {tr("Modificatore difesa attivo")}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.fairplay_attivo}
                disabled={editLocked}
                onChange={(e) => update("fairplay_attivo", e.target.checked)}
              />
              {tr("Fairplay attivo")}
            </label>
            <label className="text-sm">
              {tr("Voto rinvio SA (6 politico)")}
              <input
                type="number"
                step="0.1"
                value={draft.voto_giocatori_rinvio_sa}
                disabled={editLocked}
                onChange={(e) => update("voto_giocatori_rinvio_sa", e.target.value)}
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
              />
            </label>
            <label className="text-sm">
              {tr("Max sostituzioni")}
              <input
                type="number"
                value={draft.max_sostituzioni ?? ""}
                disabled={editLocked}
                onChange={(e) =>
                  update("max_sostituzioni", e.target.value ? parseInt(e.target.value) : null)
                }
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
              />
            </label>
            <label className="text-sm">
              {tr("Numero panchinari")}
              <input
                type="number"
                min={1}
                max={15}
                value={draft.numero_panchinari}
                disabled={editLocked}
                onChange={(e) =>
                  update(
                    "numero_panchinari",
                    Math.max(1, Math.min(15, parseInt(e.target.value) || 1)),
                  )
                }
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
              />
            </label>
          </div>

          {/* ============================================================ */}
          {/* FORMAZIONE & SOSTITUZIONI */}
          {/* ============================================================ */}
          <div className="card grid grid-cols-1 md:grid-cols-2 gap-3">
            <h3 className="font-display text-lg text-accent col-span-full">
              {tr("Formazione & sostituzioni")}
            </h3>
            <label className="text-sm">
              {tr("Comportamento voto S.V.")}
              <select
                value={draft.sv_comportamento}
                disabled={editLocked}
                onChange={(e) => update("sv_comportamento", e.target.value)}
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
              >
                {SV_COMPORTAMENTO_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              {tr("Struttura panchina")}
              <select
                value={draft.panchina_struttura}
                disabled={editLocked}
                onChange={(e) => update("panchina_struttura", e.target.value)}
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
              >
                {PANCHINA_STRUTTURA_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              {tr("Formazione non schierata")}
              <select
                value={draft.formazione_non_schierata}
                disabled={editLocked}
                onChange={(e) => update("formazione_non_schierata", e.target.value)}
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
              >
                {FORMAZIONE_NON_SCHIERATA_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </label>
            {draft.formazione_non_schierata === "punteggio_fisso" && (
              <label className="text-sm">
                {tr("Punteggio fisso (form. non schierata)")}
                <input
                  type="number"
                  step="0.1"
                  value={draft.formazione_non_schierata_punteggio_fisso ?? ""}
                  disabled={editLocked}
                  onChange={(e) =>
                    update("formazione_non_schierata_punteggio_fisso", e.target.value || null)
                  }
                  className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
                />
              </label>
            )}
          </div>

          {/* ============================================================ */}
          {/* SPAREGGI KO */}
          {/* ============================================================ */}
          <div className="card grid grid-cols-1 md:grid-cols-2 gap-3">
            <h3 className="font-display text-lg text-accent col-span-full">
              {tr("Spareggi KO (CL/Supercoppa)")}
            </h3>
            <label className="text-sm">
              {tr("Tipo spareggio")}
              <select
                value={draft.spareggio_ko}
                disabled={editLocked}
                onChange={(e) => update("spareggio_ko", e.target.value)}
                className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
              >
                {SPAREGGIO_KO_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 self-end pb-1">
              <input
                type="checkbox"
                checked={draft.spareggio_supplementari_attivo}
                disabled={editLocked}
                onChange={(e) => update("spareggio_supplementari_attivo", e.target.checked)}
              />
              {tr("Supplementari attivi")}
            </label>
            {draft.spareggio_supplementari_attivo && (
              <>
                <label className="text-sm">
                  {tr("Modalità supplementari")}
                  <select
                    value={draft.spareggio_supplementari_modalita}
                    disabled={editLocked}
                    onChange={(e) => update("spareggio_supplementari_modalita", e.target.value)}
                    className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
                  >
                    {SPAREGGIO_SUPP_MODALITA_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v}>
                        {o.l}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  {tr("N. panchinari supplementari")}
                  <input
                    type="number"
                    min={0}
                    max={15}
                    value={draft.spareggio_supplementari_n_panchinari}
                    disabled={editLocked}
                    onChange={(e) =>
                      update("spareggio_supplementari_n_panchinari", parseInt(e.target.value) || 0)
                    }
                    className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
                  />
                </label>
              </>
            )}
          </div>

          {/* ============================================================ */}
          {/* BONUS/MALUS EVENTI */}
          {/* ============================================================ */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg text-accent">{tr("Bonus / Malus eventi")}</h3>
              <button
                type="button"
                disabled={editLocked}
                onClick={() =>
                  update("bonus_malus", [...draft.bonus_malus, { evento: "gol", punteggio: "0.0" }])
                }
                className="btn-ghost text-sm"
              >
                {tr("+ aggiungi")}
              </button>
            </div>
            <div className="space-y-2">
              {draft.bonus_malus.map((bm, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select
                    value={bm.evento}
                    disabled={editLocked}
                    onChange={(e) => {
                      const c = [...draft.bonus_malus];
                      c[i] = { ...c[i], evento: e.target.value as BonusMalusItem["evento"] };
                      update("bonus_malus", c);
                    }}
                    className="flex-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
                  >
                    {EVENTI.map((e) => (
                      <option key={e} value={e}>
                        {EVENTO_LABEL[e] ?? etichetta(e)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.1"
                    value={bm.punteggio}
                    disabled={editLocked}
                    onChange={(e) => {
                      const c = [...draft.bonus_malus];
                      c[i] = { ...c[i], punteggio: e.target.value };
                      update("bonus_malus", c);
                    }}
                    className="w-24 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                  />
                  <button
                    type="button"
                    disabled={editLocked}
                    onClick={() =>
                      update(
                        "bonus_malus",
                        draft.bonus_malus.filter((_, j) => j !== i),
                      )
                    }
                    className="text-danger px-2"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ============================================================ */}
          {/* SOGLIE GOL DA FANTAPUNTI */}
          {/* ============================================================ */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg text-accent">{tr("Soglie gol da fantapunti")}</h3>
              <button
                type="button"
                disabled={editLocked}
                onClick={() =>
                  update("soglie_gol", [
                    ...draft.soglie_gol,
                    { punteggio_minimo: "66.0", gol_assegnati: 1 },
                  ])
                }
                className="btn-ghost text-sm"
              >
                {tr("+ aggiungi")}
              </button>
            </div>
            <div className="text-xs text-fg-muted mb-2">
              {tr("Da N fantapunti totali la squadra fa M gol. Ordine: ascendente per punteggio.")}
            </div>
            {/* Generatore automatico della scaletta (Andrea 2026-09-09). */}
            <div className="mb-3 p-2 rounded-lg bg-primary-deep/40 border border-primary-soft flex flex-wrap items-center gap-2 text-xs">
              <span className="text-fg-muted">{tr("Primo gol da")}</span>
              <input
                type="number"
                step="0.5"
                value={sogliaPrimoGol}
                disabled={editLocked}
                onChange={(e) => setSogliaPrimoGol(e.target.value)}
                className="w-20 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
              />
              <span className="text-fg-muted">{tr("pt, poi uno ogni")}</span>
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={passoGol}
                disabled={editLocked}
                onChange={(e) => setPassoGol(e.target.value)}
                className="w-20 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
              />
              <span className="text-fg-muted">{tr("pt, fino a")}</span>
              <input
                type="number"
                min="1"
                max="30"
                value={quantiGol}
                disabled={editLocked}
                onChange={(e) => setQuantiGol(e.target.value)}
                className="w-16 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
              />
              <span className="text-fg-muted">{"gol"}</span>
              <button
                type="button"
                disabled={editLocked}
                onClick={generaSoglieGol}
                className="ml-auto px-3 py-1.5 rounded-lg font-bold bg-accent text-ink hover:opacity-90 disabled:opacity-40 transition"
              >
                {tr("Genera scaletta")}
              </button>
            </div>
            <div className="space-y-2">
              {draft.soglie_gol.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-xs text-fg-muted w-12">{tr("Da")}</span>
                  <input
                    type="number"
                    step="0.5"
                    value={s.punteggio_minimo}
                    disabled={editLocked}
                    onChange={(e) => {
                      const c = [...draft.soglie_gol];
                      c[i] = { ...c[i], punteggio_minimo: e.target.value };
                      update("soglie_gol", c);
                    }}
                    className="w-24 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                  />
                  <span className="text-xs text-fg-muted">{tr("pt →")}</span>
                  <input
                    type="number"
                    min={0}
                    value={s.gol_assegnati}
                    disabled={editLocked}
                    onChange={(e) => {
                      const c = [...draft.soglie_gol];
                      c[i] = { ...c[i], gol_assegnati: parseInt(e.target.value) || 0 };
                      update("soglie_gol", c);
                    }}
                    className="w-16 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                  />
                  <span className="text-xs text-fg-muted">{"gol"}</span>
                  <button
                    type="button"
                    disabled={editLocked}
                    onClick={() =>
                      update(
                        "soglie_gol",
                        draft.soglie_gol.filter((_, j) => j !== i),
                      )
                    }
                    className="text-danger px-2 ml-auto"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ============================================================ */}
          {/* BONUS CAPITANO */}
          {/* ============================================================ */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg text-accent">{tr("Fattore capitano")}</h3>
              <button
                type="button"
                disabled={editLocked}
                onClick={() =>
                  update("bonus_capitano", [
                    ...draft.bonus_capitano,
                    { voto_base_minimo: "6.0", bonus_capitano: "0.0" },
                  ])
                }
                className="btn-ghost text-sm"
              >
                {tr("+ aggiungi")}
              </button>
            </div>
            <div className="text-xs text-fg-muted mb-2">
              {tr(
                "Fasce sul voto base del capitano: vale la soglia più alta raggiunta, e il valore può essere negativo (malus). La riga con minimo",
              )}{" "}
              <b>0</b>{" "}
              {tr("è il fondo scala — senza quella, sotto la prima soglia il fattore vale 0.")}
            </div>
            <div className="space-y-2">
              {draft.bonus_capitano.map((bc, i) => (
                <div key={i} className="flex gap-2 items-center">
                  {/* Andrea 2026-09-11: si legge la fascia come in Regole
                      ("≤ 4", "5.5", "≥ 8"), il campo resta il minimo. */}
                  <span
                    className="text-xs text-fg-muted w-24 truncate"
                    title={tr("Fascia risultante (il campo a fianco e' il minimo)")}
                  >
                    Voto{" "}
                    {etichettaFascia(
                      draft.bonus_capitano.map((x) => Number(x.voto_base_minimo)),
                      Number(bc.voto_base_minimo),
                    )}
                  </span>
                  <input
                    type="number"
                    step="0.1"
                    value={bc.voto_base_minimo}
                    disabled={editLocked}
                    onChange={(e) => {
                      const c = [...draft.bonus_capitano];
                      c[i] = { ...c[i], voto_base_minimo: e.target.value };
                      update("bonus_capitano", c);
                    }}
                    className="w-20 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                  />
                  <span className="text-xs text-fg-muted">{tr("→ bonus")}</span>
                  <input
                    type="number"
                    step="0.1"
                    value={bc.bonus_capitano}
                    disabled={editLocked}
                    onChange={(e) => {
                      const c = [...draft.bonus_capitano];
                      c[i] = { ...c[i], bonus_capitano: e.target.value };
                      update("bonus_capitano", c);
                    }}
                    className="w-20 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                  />
                  <button
                    type="button"
                    disabled={editLocked}
                    onClick={() =>
                      update(
                        "bonus_capitano",
                        draft.bonus_capitano.filter((_, j) => j !== i),
                      )
                    }
                    className="text-danger px-2 ml-auto"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ============================================================ */}
          {/* MODIFICATORE DIFESA */}
          {/* ============================================================ */}
          {draft.modificatore_difesa_attivo && (
            <div className="card">
              <h3 className="font-display text-lg text-accent mb-1">
                Modificatore Difesa · {NOME_BONUS_DIFENSIVO}
              </h3>
              <p className="text-[11px] text-fg-muted mb-3">
                Classic → "Modificatore Difesa" (media dei difensori). {NOME_MODALITA_PRO} → "
                {NOME_BONUS_DIFENSIVO}": media del voto base (non il fantavoto) dei migliori N
                titolari schierati nei ruoli qui sotto, di cui almeno 3 difensori{" "}
                {sigleRuoli(["Dc", "Dd", "Ds", "B"])}, più il portiere se incluso. Conta il ruolo
                dello slot in cui giocano, anche per chi subentra o dopo un cambio modulo (un{" "}
                {sigleRuoli(["M", "C"])} nello slot {siglaRuolo("M")} è un mediano). Le soglie
                media→bonus valgono per entrambe.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <label className="text-sm">
                  Giocatori nella media ({NOME_MODALITA_PRO}: i migliori N) · Classic: min.
                  difensori
                  <input
                    type="number"
                    min={0}
                    max={11}
                    value={draft.modificatore_difesa_min_difensori}
                    disabled={editLocked}
                    onChange={(e) =>
                      update("modificatore_difesa_min_difensori", parseInt(e.target.value) || 0)
                    }
                    className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm md:mt-6">
                  <input
                    type="checkbox"
                    checked={draft.modificatore_difesa_includi_portiere}
                    disabled={editLocked}
                    onChange={(e) =>
                      update("modificatore_difesa_includi_portiere", e.target.checked)
                    }
                  />
                  Includi il voto del portiere nella media ({NOME_BONUS_DIFENSIVO})
                </label>
                <label className="text-sm">
                  Ruoli difesa {NOME_MODALITA_PRO} (multi-select)
                  <select
                    multiple
                    value={draft.mantra_ruoli_difesa}
                    disabled={editLocked}
                    onChange={(e) => {
                      const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                      update("mantra_ruoli_difesa", opts);
                    }}
                    size={6}
                    className="block w-full mt-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-xs"
                  >
                    {MANTRA_RUOLI.map((r) => (
                      <option key={r} value={r}>
                        {siglaRuolo(r)}
                      </option>
                    ))}
                  </select>
                  <span className="text-[10px] text-fg-muted">
                    {tr("Ctrl/Cmd + click per selezione multipla")}
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-fg-muted">
                  {tr("Soglie media voto difesa → bonus:")}
                </span>
                <button
                  type="button"
                  disabled={editLocked}
                  onClick={() =>
                    update("modificatore_difesa", [
                      ...draft.modificatore_difesa,
                      { media_voto_minimo: "6.0", bonus: "1.0" },
                    ])
                  }
                  className="btn-ghost text-sm"
                >
                  {tr("+ aggiungi")}
                </button>
              </div>
              <div className="space-y-2">
                {draft.modificatore_difesa.map((m, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span
                      className="text-xs text-fg-muted w-28 truncate"
                      title={tr("Fascia risultante (il campo a fianco e' il minimo)")}
                    >
                      Media{" "}
                      {etichettaFascia(
                        draft.modificatore_difesa.map((x) => Number(x.media_voto_minimo)),
                        Number(m.media_voto_minimo),
                      )}
                    </span>
                    <input
                      type="number"
                      step="0.05"
                      value={m.media_voto_minimo}
                      disabled={editLocked}
                      onChange={(e) => {
                        const c = [...draft.modificatore_difesa];
                        c[i] = { ...c[i], media_voto_minimo: e.target.value };
                        update("modificatore_difesa", c);
                      }}
                      className="w-20 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                    />
                    <span className="text-xs text-fg-muted">{tr("→ bonus")}</span>
                    <input
                      type="number"
                      step="0.1"
                      value={m.bonus}
                      disabled={editLocked}
                      onChange={(e) => {
                        const c = [...draft.modificatore_difesa];
                        c[i] = { ...c[i], bonus: e.target.value };
                        update("modificatore_difesa", c);
                      }}
                      className="w-20 px-2 py-1 bg-primary-deep border border-primary-soft rounded font-mono text-sm"
                    />
                    <button
                      type="button"
                      disabled={editLocked}
                      onClick={() =>
                        update(
                          "modificatore_difesa",
                          draft.modificatore_difesa.filter((_, j) => j !== i),
                        )
                      }
                      className="text-danger px-2 ml-auto"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* FAIRPLAY */}
          {/* ============================================================ */}
          {draft.fairplay_attivo && (
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg text-accent">{tr("Fairplay")}</h3>
                <button
                  type="button"
                  disabled={editLocked}
                  onClick={() =>
                    update("fairplay", [
                      ...draft.fairplay,
                      {
                        descrizione: "",
                        zero_ammonizioni: false,
                        zero_espulsioni: false,
                        bonus: "1.0",
                      },
                    ])
                  }
                  className="btn-ghost text-sm"
                >
                  {tr("+ aggiungi")}
                </button>
              </div>
              <div className="space-y-3">
                {draft.fairplay.map((f, i) => (
                  <div key={i} className="border border-primary-soft rounded p-2 space-y-2">
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder={tr("Descrizione regola fairplay")}
                        value={f.descrizione}
                        disabled={editLocked}
                        onChange={(e) => {
                          const c = [...draft.fairplay];
                          c[i] = { ...c[i], descrizione: e.target.value };
                          update("fairplay", c);
                        }}
                        className="flex-1 px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
                      />
                      <button
                        type="button"
                        disabled={editLocked}
                        onClick={() =>
                          update(
                            "fairplay",
                            draft.fairplay.filter((_, j) => j !== i),
                          )
                        }
                        className="text-danger px-2"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="flex gap-3 items-center text-xs flex-wrap">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={f.zero_ammonizioni}
                          disabled={editLocked}
                          onChange={(e) => {
                            const c = [...draft.fairplay];
                            c[i] = { ...c[i], zero_ammonizioni: e.target.checked };
                            update("fairplay", c);
                          }}
                        />
                        {tr("Zero ammonizioni")}
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={f.zero_espulsioni}
                          disabled={editLocked}
                          onChange={(e) => {
                            const c = [...draft.fairplay];
                            c[i] = { ...c[i], zero_espulsioni: e.target.checked };
                            update("fairplay", c);
                          }}
                        />
                        {tr("Zero espulsioni")}
                      </label>
                      <label className="flex items-center gap-1 ml-auto">
                        {tr("Bonus")}
                        <input
                          type="number"
                          step="0.1"
                          value={f.bonus}
                          disabled={editLocked}
                          onChange={(e) => {
                            const c = [...draft.fairplay];
                            c[i] = { ...c[i], bonus: e.target.value };
                            update("fairplay", c);
                          }}
                          className="w-20 px-2 py-0.5 bg-primary-deep border border-primary-soft rounded font-mono"
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* SAVE BUTTON */}
          {/* ============================================================ */}
          <div className="card flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-fg-muted">
              {isNew
                ? tr("Creazione iniziale del regolamento.")
                : isCongelato
                  ? forceMode
                    ? tr(
                        "⚠ Modifica forzata abilitata — il salvataggio bypasserà il blocco congelamento.",
                      )
                    : tr("Regolamento congelato — attiva 'Modifica forzata' per salvare.")
                  : tr("Modifiche al regolamento.")}
            </div>
            <button
              type="button"
              disabled={editLocked || saveMut.isPending}
              onClick={() => saveMut.mutate(draft)}
              className="btn-primary"
            >
              {saveMut.isPending ? tr("Salvataggio…") : tr("💾 Salva regolamento")}
            </button>
          </div>
        </>
      )}
    </MobileShell>
  );
}
